<!--
    We're not allowed to assign a `ref` on a `slot` directly. The <div />
    using `display: contents;` ensures we will always have a single child from
    which we can retrieve coordinates via `getBoundingClientRect`.

    See: https://github.com/vuejs/vue/issues/7661
  -->
<template>
  <Teleport to="#main-container" :disabled="!hasTeleport">
    <div
      ref="popoverRef"
      class="wl-popover absolute z-[70]"
      :style="{
        left: `${x}px`,
        top: `${y}px`,
      }"
    >
      <Transition name="wl-popover-scale" :css="hasTransition">
        <!--
          NOTE: A `box-shadow` will not wrap around the arrow, while a
          `drop-shadow` does.
        -->
        <div
          v-if="isOpen"
          ref="popoverContainerRef"
          class="wl-popover__container drop-shadow-popover bg-default text-emphasis relative w-max font-medium"
          :class="[`wl-popover--placement-${finalPlacement}`, containerClasses]"
          tabindex="0"
        >
          <div
            v-if="hasArrow"
            ref="arrowRef"
            class="wl-popover__arrow bg-default absolute size-2 rotate-45 rounded-sm"
            :style="[
              {
                left: middlewareData?.arrow?.x
                  ? `${middlewareData.arrow?.x}px`
                  : '',
                top: middlewareData?.arrow?.y
                  ? `${middlewareData.arrow?.y}px`
                  : '',
              },
              staticArrowSideStyle,
            ]"
          />
          <slot name="content" :close="handlePopoverClose" />
        </div>
      </Transition>
    </div>
  </Teleport>
  <!-- eslint-disable-next-line vue/no-multiple-template-root -->
  <div
    ref="wrapperRef"
    :class="$attrs.class"
    class="wl-popover__wrapper contents"
    @click="handleAnchorClick"
    @mouseenter="handleAnchorMouseEnter"
    @mouseleave="handleAnchorMouseLeave"
  >
    <slot name="anchor" :is-popover-visible="isOpen" />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, useSlots, watch } from "vue";
import {
  useMutationObserver,
  onClickOutside,
  unrefElement,
} from "@vueuse/core";
import { type PopoverEmits, type PopoverProps, PopoverTrigger } from "./types";
import {
  arrow,
  autoUpdate,
  flip,
  hide,
  offset,
  shift,
  useFloating,
} from "@floating-ui/vue";

const emit = defineEmits<PopoverEmits>();
const {
  placement = "bottom",
  trigger = PopoverTrigger.HOVER,
  margin = 6,
  padding = 0,
  hasArrow = false,
  hasTeleport = false,
  // Half of the width/height of the default arrow, i.e. 8px / 2 = 4px
  arrowOffset = 4,
  isOpen = false,
  anchor,
  containerClasses,
} = defineProps<PopoverProps>();

const { DEV } = import.meta.env;
const popoverRef = ref<HTMLElement | null>(null);
const wrapperRef = ref<HTMLElement | null>(null);
const anchorRef = ref<HTMLElement | null>(null);
const arrowRef = ref<HTMLElement | null>(null);

const hasTransition = ref(true);

const handleAnchorClick = (): void => {
  if (trigger === PopoverTrigger.CLICK) {
    emit("update:isOpen", !isOpen);
  }
};

const handleAnchorMouseEnter = (): void => {
  if (trigger === PopoverTrigger.HOVER) {
    emit("update:isOpen", true);
  }
};

const handleAnchorMouseLeave = (): void => {
  if (trigger === PopoverTrigger.HOVER) {
    emit("update:isOpen", false);
  }
};

const handlePopoverClose = (): void => {
  emit("update:isOpen", false);
};

const slots = useSlots();

onMounted(() => {
  // NOTE: We expect a defined `anchor` ref as prop or via the anchor `v-slot`.
  //
  // If the `anchor` prop was used instead we do not need to check for the
  // (invalid) presence of multiple children in the `anchor` v-slot.
  if (!slots.anchor) {
    return;
  }

  if (DEV) {
    if (wrapperRef.value?.children.length !== 1) {
      throw new Error("<WLPopover /> expects a single child as anchor");
    }
  }

  const child = wrapperRef.value?.children[0];

  if (child instanceof HTMLElement) {
    anchorRef.value = child;
  }
});

// When the <slot /> element is added/removed (e.g. via `v-if`) we want to
// update the anchor.
useMutationObserver(
  wrapperRef,
  (mutations) => {
    if (DEV) {
      if (mutations.length !== 1) {
        if (wrapperRef.value?.children.length !== 1) {
          throw new Error("<WLPopover /> expects a single child as anchor");
        }

        return;
      }
    }

    const [mutation] = mutations;

    if (mutation.addedNodes.length !== 1) {
      hasTransition.value = false;
      emit("update:isOpen", false);
      return;
    }

    hasTransition.value = true;
    anchorRef.value = mutation.addedNodes[0] as HTMLElement;
  },
  {
    childList: true,
  },
);

onClickOutside(
  popoverRef,
  () => {
    if (trigger === PopoverTrigger.CLICK) {
      emit("update:isOpen", false);
    }
  },
  { ignore: [wrapperRef] },
);

const normalizedAnchor = computed(
  () => unrefElement(anchor) ?? anchorRef.value,
);

const {
  x,
  y,
  placement: finalPlacement,
  middlewareData,
  update,
} = useFloating(normalizedAnchor, popoverRef, {
  placement: () => placement,
  middleware: computed(() => [
    offset(margin),
    shift({ padding }),
    flip(),
    arrow({
      element: arrowRef,
      // NOTE: Prevents the arrow from overflowing when the popover has a
      // border-radius.
      // See: https://floating-ui.com/docs/arrow#padding
      padding: 12,
    }),
    hide(),
  ]),
  whileElementsMounted: autoUpdate,
});

watch(
  () => isOpen,
  () => {
    update();
  },
);

const staticArrowSideStyle = computed(() => {
  const [side] = finalPlacement.value.split("-");

  const staticSide = {
    top: "bottom",
    right: "left",
    bottom: "top",
    left: "right",
  }[side] as string;

  return { [staticSide]: `-${arrowOffset}px` };
});
</script>

<style>
@reference "@theme";

.wl-popover-scale-enter-to,
.wl-popover-scale-leave-from {
  @apply scale-100 opacity-100 duration-300;
}

.wl-popover-scale-enter-from,
.wl-popover-scale-leave-to {
  @apply scale-75 opacity-0 duration-300;
}

/* TODO: tailwind 4 this might be supported through config. */
.wl-popover__container {
  filter: var(--drop-shadow-popover);
}
</style>
